import { makeAutoObservable } from "mobx";

import { InvoicePaymentType } from "../models/InvoicePaymentType";
import { getPaymentTypes } from "../services/PaymentTypesAPI";
import { getActiveDepartmentCode } from "../utils/ActiveDepartment";
import { errorStore } from "./ErrorStore";

class InvoicePaymentTypeStore {
	data: InvoicePaymentType[] = [];
	isLoading = false;
	private hasLoaded = false;
	private requestVersion = 0;

	constructor() {
		makeAutoObservable(this);
	}

	async loadData() {
		if (this.hasLoaded || this.isLoading) return;
		const requestVersion = ++this.requestVersion;
		const departmentCode = getActiveDepartmentCode();

		try {
			this.setLoading(true);
			const response = await getPaymentTypes();
			if (requestVersion !== this.requestVersion || departmentCode !== getActiveDepartmentCode()) {
				return;
			}

			this.setData(response || []);
		} catch (error: any) {
			if (requestVersion === this.requestVersion && departmentCode === getActiveDepartmentCode()) {
				this.setError(error.message);
			}
		} finally {
			if (requestVersion === this.requestVersion) {
				this.setLoading(false);
			}
		}
	}

	async reloadForActiveDepartment() {
		this.reset();
		await this.loadData();
	}

	get options() {
		return this.data.map(paymentType => ({
			id: paymentType.name,
			name: paymentType.name,
		}));
	}

	findByImportValue(value?: string) {
		const cleanedValue = value?.trim();
		if (!cleanedValue) return undefined;

		const normalizedValue = cleanedValue.toUpperCase();
		return this.data.find(paymentType => paymentType.name.toUpperCase() === normalizedValue)?.name;
	}

	reset() {
		this.requestVersion++;
		this.data = [];
		this.hasLoaded = false;
		this.isLoading = false;
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	setData(data: InvoicePaymentType[]) {
		this.data = data;
		this.hasLoaded = true;
	}

	setError(error: string) {
		errorStore.report(error);
	}
}

export const invoicePaymentTypeStore = new InvoicePaymentTypeStore();
